import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { settingsService } from 'v2/shared/services/settings-service'
import { getSettingsQueryKeys } from 'v2/shared/utils/query-key-management'
import { useAppStore } from 'v2/state/app'
import type {
  RAgentStatusUpdate,
  ROrgStatus,
  RStatusConfigurations,
  RStatusList,
} from 'v2/types/api/settings'
import type { AgentStatusFormFields } from './sheet'

export const useOrgStatusQuery = <T = ROrgStatus[]>(select?: (data: ROrgStatus[]) => T) =>
  useQuery({
    queryKey: getSettingsQueryKeys().status,
    meta: {
      silent: true,
    },
    queryFn: async () => {
      const response = await settingsService.getStatus()
      return response.data.org_statuses
    },
    select,
  })

export const useCurrentStatusQuery = () => {
  const { agentStatus } = useAppStore()
  const q = useOrgStatusQuery((data) => data.find((s) => s.uuid === agentStatus.uuid))
  return q
}

export const useOrgStatusReorderMutation = () => {
  const queryClient = useQueryClient()
  const updateData = (statuses: ROrgStatus[]) =>
    queryClient.setQueryData(getSettingsQueryKeys().status, (data: RStatusList) => {
      if (data) return statuses
      return data
    })

  return useMutation({
    mutationFn: async (statuses: ROrgStatus[]) => {
      const response = await settingsService.reorderStatus(statuses.map((status) => status.uuid))
      return response.data
    },
    onMutate: (statuses: ROrgStatus[]) => {
      const previousData = queryClient.getQueryData<ROrgStatus[]>(getSettingsQueryKeys().status)
      updateData(statuses)
      return { previousData }
    },
    onError: (_, _variables, context) => {
      if (context?.previousData) updateData(context.previousData)
    },
  })
}

export const useAgentStatusUpdateMutation = () =>
  useMutation({
    mutationFn: async (status: RAgentStatusUpdate) => {
      const response = await settingsService.updateAgentStatus(status)
      return response.data
    },
    onSuccess: (_d, data) => {
      useAppStore.getState().setAgentStatus({ uuid: data.status_uuid, updateType: 'manual' })
    },
  })

export const useMarkInactiveMutation = () =>
  useMutation({
    mutationFn: async () => {
      const response = await settingsService.markInactive()
      return response.data
    },
    retry: 3,
    onSuccess: (d) => {
      useAppStore.getState().setAgentStatus({ uuid: d.uuid, updateType: 'idle' })
    },
  })

export const useOrgStatusUpsertMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (status: AgentStatusFormFields) => {
      const response = await settingsService.upsertStatus(status)
      return response.data
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().status }),
  })
}

export const useOrgStatusConfigQuery = () =>
  useQuery({
    queryKey: getSettingsQueryKeys().statusConfigurations,
    queryFn: async () => {
      const response = await settingsService.getStatusConfigurations()
      return response.data
    },
  })

export const useOrgStatusConfigMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (config: RStatusConfigurations) => {
      const response = await settingsService.updateStatusConfigurations(config)
      return response.data
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().statusConfigurations }),
  })
}
export const useOrgStatusDeleteMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (uuid: string) => {
      const response = await settingsService.deleteStatus(uuid)
      return response.data
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().status })
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().statusConfigurations })
    },
  })
}
